import { IEndpoint } from 'interfaces/IEndPoint';
import AuthProtect from 'common/enums/AuthProtect';
import { IIncident } from 'interfaces/incident.interface';
import { ApiIncidentsFilters } from './requestEndPoints';

/**
 * Interface representing the filters for managing incidents in the API.
 *
 * @interface ApiIncidentManageFilters
 * @property {string} [keyword] - The search keyword for filtering incidents.
 * @property {number} [limit] - The maximum number of incidents to retrieve.
 * @property {number} [offset] - The starting point for pagination.
 * @property {string} [orderBy] - The field to order the results by.
 * @property {number} [archived] - Filter for archived incidents (1 = archived, 0 = not archived).
 */
export interface ApiIncidentManageFilters {
  keyword?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  archived?: number;
}

/**
 * Helper function to set the parameters for the incidents API request.
 *
 * @param {ApiIncidentManageFilters} filters - The filters to apply to the API request.
 * @returns {ApiIncidentManageFilters} The processed parameters for the API request.
 */
function setIncidentsParams(filters: ApiIncidentManageFilters): ApiIncidentManageFilters {
  const params: ApiIncidentManageFilters = {};

  if (filters.offset || filters.offset === 0) {
    params.offset = filters.offset;
  }

  if (filters.limit) {
    params.limit = filters.limit;
  }

  if (filters.archived) {
    params.archived = filters.archived;
  }

  return params;
}

/**
 * Creates an endpoint configuration for adding a new incident.
 *
 * @param {IIncident} data - The incident data to add.
 * @returns {IEndpoint} The endpoint configuration for the API request.
 */
export const addIncident = (data: IIncident): IEndpoint => {
  return {
    authorization: AuthProtect.REQUIRED,
    method: 'POST',
    url: 'incident',
    data,
  };
};

/**
 * Creates an endpoint configuration for updating an existing incident.
 *
 * @param {IIncident} data - The updated incident data.
 * @param {string} id - The ID of the incident to update.
 * @returns {IEndpoint} The endpoint configuration for the API request.
 */
export const updateIncident = (data: IIncident, id: string): IEndpoint => {
  const params = { id };
  return {
    authorization: AuthProtect.REQUIRED,
    method: 'PUT',
    url: 'incident',
    params,
    data,
  };
};

/**
 * Creates an endpoint configuration for retrieving a list of incidents.
 *
 * @param {ApiIncidentsFilters} filters - The filters to apply to the API request.
 * @returns {IEndpoint} The endpoint configuration for the API request.
 */
export const getIncidents = (filters: ApiIncidentsFilters): IEndpoint => {
  const params = setIncidentsParams(filters);
  return {
    authorization: AuthProtect.REQUIRED,
    method: 'GET',
    url: 'incident',
    params: params,
  };
};

/**
 * Creates an endpoint configuration for retrieving a specific incident by its ID.
 *
 * @param {string} id - The ID of the incident to retrieve.
 * @returns {IEndpoint} The endpoint configuration for the API request.
 */
export const getIncidentById = (id: string): IEndpoint => {
  return {
    authorization: AuthProtect.REQUIRED,
    url: `incident/${id}`,
    method: 'GET',
  };
};

/**
 * Creates an endpoint configuration for archiving an incident.
 *
 * @param {{ id: string }} data - The ID of the incident to archive.
 * @returns {IEndpoint} The endpoint configuration for the API request.
 */
export const archiveIncident = (data: { id: string }): IEndpoint => {
  return {
    authorization: AuthProtect.REQUIRED,
    method: 'POST',
    url: 'incident/archive',
    data,
  };
};
